/* eslint-disable max-lines -- This public API contract stays centralized to make cross-service field drift reviewable. */
/**
 * API client for enhanced forest visualization endpoints.
 *
 * The simulation API was migrated to the canonical run/branch shape.
 * Submission returns a ``run_id``; the client polls
 * ``GET /runs/{run_id}`` for the lifecycle status (SUBMITTED / RUNNING /
 * COMPLETED / FAILED) and reads ``GET /runs/{run_id}/branches/{branch_id}``
 * for the time-series payload.  ``NO_MANAGEMENT`` runs always have exactly
 * one branch.
 */

import { apiClient, simulationApiClient, classifyError, getResponseStatus } from './client';
import type { NpvLowerBoundMarker } from '@/design-system/visual-grammar/reportNpvLowerBoundNotices';
import {
  DetailedForestStand,
  EnhancedVisualizationResponse,
  PropertyOverviewData,
} from '@/types/forest-detailed';
import type { ForestManagementOperation } from '@/types/operations';
import type { StandCorrection, StandStratumCorrectionBatchResponse } from '@/types/measurements';
import type { StandMeasurementHistoryPoint } from '@/components/forest-analysis/StandMeasurementHistoryPanel';
import type { SimulationRequestParameters as GeneratedSimulationRequestParameters } from '@/types/generated/forest_models';
import {
  measurementStandContextSnapshotSchema,
  type MeasurementStandContextSnapshot,
} from '@/api/measurementsSnapshot.schemas';
import type { SimulationValueGrowthSeries } from '@/utils/simulationChartProcessor';
import {
  normalizeDetailedForestStand,
  type ResolvedStandStatePayload,
} from '@/utils/standContract';

export interface RunSubmissionResponse {
  run_id: string;
  status: 'created' | 'deduplicated';
}

export interface SelectedPlanValueGrowthContribution {
  run_id: string;
  branch_id: string;
  economics_available: boolean;
  annual_value_growth_eur: number | null;
  starting_value_eur: number | null;
  period_years: number | null;
  unavailability_reason: string | null;
}

export interface SelectedPlanValueGrowthResponse {
  property_id: string;
  snapshot_id: string;
  contributions: SelectedPlanValueGrowthContribution[];
}

export interface BundleSliceError {
  status: number;
  detail: string;
}

export type BundleSliceName =
  | 'corrections'
  | 'stratum_corrections'
  | 'history'
  | 'measurement_context'
  | 'operations'
  | 'baseline'
  | 'resolved';

export type StandDetailBundleErrors = Partial<Record<BundleSliceName, BundleSliceError>>;

export interface BranchWarningContext {
  decision_year?: number;
  branch_id?: string;
  method?: string;
  species_code?: string;
  site_class?: string;
}

export interface SimulationBranchWarning {
  code: string;
  severity: string;
  /**
   * Story copy key of the owner sentence, derived by the server from `code`;
   * absent for an INTERNAL engine diagnostic, which the app never renders.
   */
  message_key?: string | null;
  message_params: Record<string, unknown>;
  context: BranchWarningContext;
  source_citation: string;
  suggestion?: string;
  /**
   * Identifier of the literature record backing `source_citation`
   * (`forest_models.branch_warnings.SimulationBranchWarning.citation_id`).
   * Nullable on the wire, so the served null is modelled rather than widened
   * into "absent".
   */
  citation_id?: string | null;
  /**
   * TECHNICAL diagnostic text: it names the model, the published bound that was
   * violated, and the paper the bound comes from. Never render it to an owner —
   * owner-facing wording is the story copy `message_key` names.
   */
  message: string;
}

export interface StandDetailBundleResponse {
  resolved: ResolvedStandStatePayload | null;
  baseline: DetailedForestStand | null;
  corrections: StandCorrection | null;
  stratum_corrections: StandStratumCorrectionBatchResponse | null;
  measurement_history: StandMeasurementHistoryPoint[] | null;
  measurement_context: MeasurementStandContextSnapshot | null;
  operations: ForestManagementOperation[] | null;
  bias_warnings: SimulationBranchWarning[];
  errors: StandDetailBundleErrors | null;
}

export interface BranchSummary {
  branch_id: string;
  parent_branch_id: string | null;
  fork_year: number | null;
  fork_decision: string;
  depth: number;
  is_terminal: boolean;
  summary_json: Record<string, unknown>;
  warnings?: SimulationBranchWarning[];
  // Promoted typed fields from summary_json.
  // Optional because older persisted runs and test fixtures may not carry them.
  year_of_first_cut?: number | null;
  rotation_length?: number | null;
  total_harvest_volume_m3?: number | null;
  total_sawlog_m3?: number | null;
  total_pulp_m3?: number | null;
  total_revenue_eur?: number | null;
  total_cost_eur?: number | null;
  terminal_stand_value_eur?: number | null;
  final_npv_eur?: number | null;
  first_action_type?: string | null;
  /** Terminal-inclusive branch ranking NPV in EUR/ha. */
  ranking_npv_eur?: number | null;
  pricing_snapshot_id?: string | null;
  discount_rate?: number | null;
  /** Active-snapshot terminal-inclusive economics in EUR/stand. */
  active_npv_eur?: number | null;
  active_revenue_eur?: number | null;
  active_cost_eur?: number | null;
  active_terminal_stand_value_eur?: number | null;
  /** The active NPV's served lower-bound marker; null when none was stated. */
  active_npv_lower_bound?: NpvLowerBoundMarker | null;
  economics_basis?: 'active_snapshot' | 'unavailable';
  has_clearfell?: boolean;
}

/** Lightweight run descriptor for the stand run list. */
export interface SimulationDeletionDestination {
  kind: 'standalone_run' | 'simulation_batch' | 'saved_plan';
  run_id: string;
  batch_id: string | null;
  saved_plan_id: string | null;
  affected_simulation_count: number;
  blocked_reason:
    | 'simulation_run_in_flight'
    | 'batch_in_flight'
    | 'simulation_is_stand_choice'
    | 'saved_plan_simulations_referenced'
    | 'saved_plan_lineage_protected'
    | 'saved_plan_reference'
    | null;
  blocking_saved_plan_ids: string[];
}

export interface StandRunListItem {
  run_id: string;
  decision_mode: string;
  status: string;
  branch_count: number;
  best_branch_id?: string | null;
  parameters_json: Record<string, unknown> | null;
  created_at: string | null;
  completed_at: string | null;
  deletion_destination?: SimulationDeletionDestination;
}

export interface StandRunListResponse {
  property_id: string;
  stand_id: string;
  runs: StandRunListItem[];
  total: number;
}

export interface DeleteRunResponse {
  run_id: string;
  deleted: boolean;
}

/** Neighboring property stand for the inspection map. */
export interface NearbyPropertyStand {
  stand_id: string;
  dominant_species_code: string | null;
  area_hectares: number | null;
  geometry_geojson: Record<string, unknown> | null;
}

export type RunLifecycleStatus = 'SUBMITTED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface RunDetail {
  run_id: string;
  property_id: string;
  stand_id: string;
  batch_id: string | null;
  decision_mode: string;
  parameters_hash: string;
  /**
   * Full request inputs (G_min, discount_rate, etc.) stored when the run was
   * submitted.  Populated from the SimulationRun.parameters_json DB column so
   * the provenance badge can display the exact parameters used (AC6).
   */
  parameters_json: Record<string, unknown> | null;
  status: RunLifecycleStatus;
  branch_count: number;
  branches: BranchSummary[];
  warnings?: SimulationBranchWarning[];
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  error_detail?: Record<string, unknown> | null;
}

export interface TimeStepRow {
  year: number;
  stand_state_json_v2: Record<string, unknown> | null;
  rotation_stage: string | null;
  management_operation: string | null;
  operation_subtype: string | null;
  removed_volume_m3: number | null;
  removed_revenue_eur?: number | null;
  operation_cost_eur?: number | null;
  cumulative_yield_m3: number;
  // Authoritative non-euro harvest/regeneration fields
  // (backend TimeStepRow @ backend_simulation/app/api/routes/runs.py:429-440).
  // These carry the per-timestep VOLUME assortment split + regeneration plan and
  // are the source the branch-driven Operations panel renders. Euros are NEVER
  // sourced here — the sim payload's removed_revenue_eur/operation_cost_eur above
  // are declared (legacy/forbidden) but not consumed; authoritative economics are
  // derived elsewhere (docs/MONETARY_CONTRACT.md).
  /** Regeneration species code (forest_models 1-30) for a regeneration timestep; null otherwise. */
  species_code?: string | null;
  /** Target regeneration density per hectare; null when no regeneration. */
  density_per_ha?: number | null;
  /** Removed sawlog volume in this timestep (m³/ha); null when no harvest/assortment. */
  removed_sawlog_m3_per_ha?: number | null;
  /** Removed pulpwood volume in this timestep (m³/ha); null when no harvest/assortment. */
  removed_pulpwood_m3_per_ha?: number | null;
  /** Removed energy-wood volume in this timestep (m³/ha); null when no harvest/assortment. */
  removed_energy_m3_per_ha?: number | null;
}

export interface BranchDetail {
  branch_id: string;
  run_id: string;
  /** Served simulation epoch. Relative timestep years resolve as start_year + year. */
  start_year?: number | null;
  parent_branch_id: string | null;
  fork_year: number | null;
  fork_decision: string;
  depth: number;
  is_terminal: boolean;
  summary_json: Record<string, unknown>;
  warnings?: SimulationBranchWarning[];
  time_series: TimeStepRow[];
}

export type SimulationRequestParameters = GeneratedSimulationRequestParameters & {
  enable_method_alternatives?: boolean;
  enable_thinning_intensity_alternatives?: boolean;
};

export type StandPreflightFieldStatus =
  | 'required-present'
  | 'required-missing'
  | 'optional-derived'
  | 'optional-user-supplied'
  | 'optional-unassessed';

export interface StandSimulationPreflightField {
  name: string;
  status: StandPreflightFieldStatus;
  value: unknown;
  provenance: string | null;
}

export interface StandSimulationPreflightResponse {
  ready: boolean;
  fields: StandSimulationPreflightField[];
  /** Why readiness could not be assessed at all. Written for a person. */
  not_ready_reason?: string | null;
  /** Stable token for the same cause, for clients that must branch on it. */
  not_ready_reason_code?: string | null;
}

export type StandDetailBundleInclude =
  | 'corrections'
  | 'stratum_corrections'
  | 'history'
  | 'measurement_context'
  | 'operations'
  | 'bias_warnings';

export interface StandDetailBundleOptions {
  include?: StandDetailBundleInclude[];
  limit?: number;
  language?: string;
}

function buildStandDetailBundleParams(
  options?: StandDetailBundleOptions,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options?.include && options.include.length > 0) params.include = options.include.join(',');
  if (typeof options?.limit === 'number') params.limit = options.limit;
  if (options?.language) params.language = options.language;
  return params;
}

function normalizeStandDetailBundle(
  payload: Partial<StandDetailBundleResponse>,
): StandDetailBundleResponse {
  return {
    resolved: (payload.resolved as ResolvedStandStatePayload | null) ?? null,
    baseline: payload.baseline
      ? normalizeDetailedForestStand(payload.baseline as DetailedForestStand)
      : null,
    corrections: (payload.corrections as StandCorrection | null) ?? null,
    stratum_corrections:
      (payload.stratum_corrections as StandStratumCorrectionBatchResponse | null) ?? null,
    measurement_history: Array.isArray(payload.measurement_history)
      ? payload.measurement_history
      : null,
    measurement_context: payload.measurement_context
      ? measurementStandContextSnapshotSchema.parse(payload.measurement_context)
      : null,
    operations: Array.isArray(payload.operations) ? payload.operations : null,
    bias_warnings: Array.isArray(payload.bias_warnings) ? payload.bias_warnings : [],
    errors:
      payload.errors && typeof payload.errors === 'object'
        ? (payload.errors as StandDetailBundleErrors)
        : null,
  };
}

/**
 * Request budget for the two reads backed by the property snapshot's cold pass.
 *
 * A property's FIRST read enriches every stand from the MML elevation model,
 * bounded to 3 concurrent reads per backend worker.
 * Measured cold on that branch with the local DEM off: 124 stands = 12.8 s wall
 * clock (124 MML calls, peak 3 in flight, 0.19 s p50 / 0.48 s p95 per call);
 * 65 stands = 7.5 s. The same read warm is 0.06 s, because elevations are
 * cached per coordinate for 30 days, so only a property's first load pays it.
 * Extrapolating the measured mean (0.246 s per call at 3 in flight), a
 * 250-stand property costs ~20 s, and ~42 s at the measured p95 — which the
 * shared 30 s apiClient default would abort mid-flight.
 *
 * `/detailed` and `/overview` BOTH go through `get_property_snapshot`, so they
 * share this budget: giving it to only one leaves the same slow first load
 * failing on the other.
 */
export const SNAPSHOT_COLD_READ_TIMEOUT_MS = 90_000;

export class EnhancedForestAPI {
  /**
   * Get detailed forest data for a property
   */
  static async getDetailedVisualization(
    propertyId: string,
    bufferMeters: number = 50,
    signal?: AbortSignal,
    options?: { omitAuthorization?: boolean },
  ): Promise<EnhancedVisualizationResponse> {
    const omitAuthorization = options?.omitAuthorization === true;
    const response = await apiClient.get(`/api/v1/property-overview/${propertyId}/detailed`, {
      params: {
        buffer_meters: bufferMeters,
      },
      timeout: SNAPSHOT_COLD_READ_TIMEOUT_MS,
      skipErrorLogging: true,
      skipAuth401: omitAuthorization,
      omitAuthorization,
      signal,
    });
    return {
      ...response.data,
      detailed_stands: (response.data.detailed_stands ?? []).map(normalizeDetailedForestStand),
    };
  }

  /**
   * Get property overview data
   */
  static async getPropertyOverview(propertyId: string): Promise<PropertyOverviewData> {
    const response = await apiClient.get(`/api/v1/property-overview/${propertyId}/overview`, {
      timeout: SNAPSHOT_COLD_READ_TIMEOUT_MS,
    });
    return response.data;
  }

  /**
   * Get detailed stand information
   */
  static async getStandDetail(
    propertyId: string,
    standId: string,
    language?: string,
  ): Promise<DetailedForestStand> {
    const response = await apiClient.get(
      `/api/v1/stand-detail/${propertyId}/stand/${standId}`,
      language ? { params: { language } } : undefined,
    );
    return normalizeDetailedForestStand(response.data);
  }

  /**
   * Get consolidated stand detail mount payload.
   */
  static async getStandDetailBundle(
    propertyId: string,
    standId: string,
    options?: StandDetailBundleOptions,
  ): Promise<StandDetailBundleResponse> {
    const response = await apiClient.get(`/api/v1/stands/${propertyId}/${standId}/detail-bundle`, {
      params: buildStandDetailBundleParams(options),
    });
    return normalizeStandDetailBundle((response.data ?? {}) as Partial<StandDetailBundleResponse>);
  }

  /**
   * Get cached visualization data
   */
  static async getCachedVisualization(
    visualizationId: string,
  ): Promise<EnhancedVisualizationResponse> {
    const response = await apiClient.get(`/api/v1/visualization/${visualizationId}`);
    return response.data;
  }

  /**
   * Submit a forest stand simulation run.
   *
   * Migrated to the canonical ``POST /runs`` shape.
   * The backend dedups by parameters_hash within a 24h window — re-submitting
   * the same payload returns the existing run_id with status='deduplicated'.
   *
   * ``regeneration_random_state`` is intentionally not surfaced by the current
   * StandDetail modal UI; it remains available here for direct API consumers.
   */
  static async submitStandSimulation(
    propertyId: string,
    standId: string,
    simulationParams: SimulationRequestParameters = {},
  ): Promise<RunSubmissionResponse> {
    const response = await simulationApiClient.post(
      `/api/v1/stand-simulation/${propertyId}/stand/${standId}/simulate`,
      simulationParams,
    );
    return response.data;
  }

  /**
   * Get stand simulation preflight checklist.
   *
   * Endpoint surfaces required-missing fields before simulation submit so the
   * UI can block invalid runs and guide users to corrections.
   */
  static async getStandSimulationPreflight(
    propertyId: string,
    standId: string,
  ): Promise<StandSimulationPreflightResponse> {
    const response = await apiClient.get(
      `/api/v1/stand-simulation/${propertyId}/stand/${standId}/preflight`,
      {
        skipErrorLogging: (error: unknown) => getResponseStatus(error) === 404,
      },
    );
    return response.data;
  }

  /**
   * Get the run header (status + branch summaries).
   *
   * Replaces ``getStandSimulationStatus`` — the canonical surface returns
   * status + summaries in a single call.  Throws a ClassifiedError so
   * callers can distinguish transient errors (503, timeout) from permanent
   * ones (4xx).
   */
  static async getStandSimulationRun(
    propertyId: string,
    standId: string,
    runId: string,
  ): Promise<RunDetail> {
    try {
      const response = await apiClient.get(
        `/api/v1/stand-simulation/${propertyId}/stand/${standId}/runs/${runId}`,
      );
      return response.data;
    } catch (error: unknown) {
      throw classifyError(error);
    }
  }

  /**
   * Get the time series for one branch of a run.
   *
   * Replaces ``getStandSimulationResults``.  ``NO_MANAGEMENT`` runs have
   * exactly one branch; AAF runs may have many.
   */
  static async getStandSimulationBranch(
    propertyId: string,
    standId: string,
    runId: string,
    branchId: string,
    signal?: AbortSignal,
  ): Promise<BranchDetail> {
    const response = await simulationApiClient.get(
      `/api/v1/stand-simulation/${propertyId}/stand/${standId}/runs/${runId}/branches/${branchId}`,
      { signal },
    );
    return response.data;
  }

  /**
   * Fetch on-demand value-growth series for a branch under an active snapshot.
   *
   * the branch endpoint never emits value_growth_series
   * on its summary_json, so this dedicated call computes it live from persisted
   * branch strata + the active pricing snapshot.
   *
   * Routed through the gateway simulation proxy:
   *   GET /api/simulations/runs/{runId}/branches/{branchId}/value-growth?snapshot_id=...
   *
   * Returns the raw BranchValueGrowthResponse dict (value_growth_series is the
   * StandValueGrowthSeries serialized as JSON).
   */
  static async getBranchValueGrowth(
    runId: string,
    branchId: string,
    snapshotId: string,
  ): Promise<{
    branch_id: string;
    run_id: string;
    snapshot_id: string;
    value_growth_series: SimulationValueGrowthSeries;
  }> {
    const response = await apiClient.get(
      `/api/simulations/runs/${runId}/branches/${branchId}/value-growth`,
      { params: { snapshot_id: snapshotId }, skipAuth401: true, skipErrorLogging: true },
    );
    return response.data;
  }

  /** Fetch the selected plan's compact value-growth contributions in one request. */
  static async getSelectedPlanValueGrowth(
    propertyId: string,
    selections: Array<{ run_id: string; branch_id: string }>,
    snapshotId: string,
    signal?: AbortSignal,
  ): Promise<SelectedPlanValueGrowthResponse> {
    const response = await apiClient.post(
      `/api/simulations/properties/${encodeURIComponent(propertyId)}/selected-plan-value-growth`,
      { snapshot_id: snapshotId, selections },
      { skipAuth401: true, skipErrorLogging: true, signal },
    );
    return response.data;
  }

  // Stand run list and delete

  /** List completed simulation runs for a stand, most-recent first. */
  static async listStandSimulationRuns(
    propertyId: string,
    standId: string,
    limit: number = 20,
  ): Promise<StandRunListResponse> {
    const response = await apiClient.get(
      `/api/v1/stand-simulation/${propertyId}/stand/${standId}/runs`,
      { params: { limit } },
    );
    return response.data;
  }

  /** Delete a simulation run and all cascaded branch/timestep data. */
  static async deleteStandSimulationRun(
    propertyId: string,
    standId: string,
    runId: string,
  ): Promise<DeleteRunResponse> {
    const response = await apiClient.delete(
      `/api/v1/stand-simulation/${propertyId}/stand/${standId}/runs/${runId}`,
    );
    return response.data;
  }

  // Nearby property stands

  /** Return neighboring stands for the inspection map, colored by dominant species. */
  static async getNearbyPropertyStands(
    propertyId: string,
    standId: string,
  ): Promise<NearbyPropertyStand[]> {
    const response = await apiClient.get(
      `/api/v1/stands/${propertyId}/${standId}/nearby-property-stands`,
    );
    return response.data;
  }

  // Branch-comparison query API (plan §6).
  //
  // These endpoints go through the FastAPI gateway simulation proxy, which
  // is mounted at /api/simulations in main.py.  The full gateway paths are:
  //
  //   GET  /api/simulations/runs/{runId}/branches/best?criterion=...
  //   GET  /api/simulations/runs/{runId}/branches/top?criterion=...&n=...
  //   GET  /api/simulations/runs/{runId}/branches/with-first-action?year=...&action=...
  //   POST /api/simulations/runs/{runId}/branches/compare  (body: {branch_ids: [...]})
  //
  // We use simulationApiClient (5-min timeout) for the compare POST which
  // may pull large time-series payloads, and apiClient (30-s timeout) for
  // the lightweight GET queries.

  /** Best branch under one criterion (single result). */
  static async getBestBranch(runId: string, criterion: BranchCriterion): Promise<BranchSummary> {
    const response = await apiClient.get(`/api/simulations/runs/${runId}/branches/best`, {
      params: { criterion },
    });
    return response.data;
  }

  /** Top-N branches under one criterion (sorted best→worst). */
  static async getTopNBranches(
    runId: string,
    criterion: BranchCriterion,
    n: number = 5,
  ): Promise<BranchSummary[]> {
    const response = await apiClient.get(`/api/simulations/runs/${runId}/branches/top`, {
      params: { criterion, n },
    });
    return response.data;
  }

  /**
   * All branches whose first matching action lands on ``year``.
   *
   * The endpoint always returns a LIST — multiple branches in the tree may
   * share the same first-action year (different downstream forks).  The UI
   * is responsible for choosing among them by a secondary criterion.
   */
  static async getBranchesWithFirstActionAt(
    runId: string,
    year: number,
    action: BranchActionType,
  ): Promise<BranchSummary[]> {
    const response = await apiClient.get(
      `/api/simulations/runs/${runId}/branches/with-first-action`,
      { params: { year, action } },
    );
    return response.data;
  }

  /**
   * Comparison payload for overlay charts.
   *
   * Returns the requested branches with their full time series so the
   * frontend can overlay 2+ branches on the existing volume / value
   * charts (acceptance criterion 3).  ``missing_branch_ids`` lists any
   * IDs that did not resolve so the UI can render a partial result.
   */
  static async compareBranches(
    runId: string,
    branchIds: string[],
    signal?: AbortSignal,
  ): Promise<BranchComparisonResponse> {
    const response = await simulationApiClient.post(
      `/api/simulations/runs/${runId}/branches/compare`,
      { branch_ids: branchIds },
      { signal },
    );
    return response.data;
  }

  /** Bounded comparison payload for multiple owned runs in one request. */
  static async compareBranchGroups(
    groups: BranchComparisonGroupRequest[],
    signal?: AbortSignal,
  ): Promise<BulkBranchComparisonResponse> {
    const response = await simulationApiClient.post(
      '/api/simulations/runs/branches/compare-bulk',
      { groups },
      { signal },
    );
    return response.data;
  }

  /**
   * Persist the user's chosen simulation branch for a stand.
   *
   * Validates on the backend that the run is COMPLETED and the branch belongs
   * to the run before saving.  Returns the saved choice.
   */
  static async setSimulationChoice(
    propertyId: string,
    standId: string,
    runId: string,
    branchId: string,
  ): Promise<{ simulation_job_id: string; simulation_branch_id: string }> {
    const response = await apiClient.post(
      `/api/v1/planning/stands/${propertyId}/${standId}/simulation-choice/`,
      { simulation_job_id: runId, branch_id: branchId },
    );
    return response.data;
  }
}

// Branch-comparison API types

export type BranchCriterion =
  | 'NPV'
  | 'GROSS_REVENUE'
  | 'TOTAL_COST'
  | 'YIELD'
  | 'TIME_TO_FIRST_CUT'
  | 'LONGEST_ROTATION'
  | 'TERMINAL_STAND_VALUE';

export type BranchActionType = 'CUT' | 'THIN' | 'CLEARFELL';

export interface BranchWithTimeSeries {
  branch_id: string;
  run_id: string;
  start_year?: number | null;
  parent_branch_id: string | null;
  fork_year: number | null;
  fork_decision: string;
  depth: number;
  is_terminal: boolean;
  summary_json: Record<string, unknown>;
  time_series: TimeStepRow[];
}

export interface BranchComparisonResponse {
  branches: BranchWithTimeSeries[];
  missing_branch_ids: string[];
  branch_count: number;
}

export interface BranchComparisonGroupRequest {
  run_id: string;
  branch_ids: string[];
}

export interface BranchComparisonGroupResponse extends BranchComparisonResponse {
  run_id: string;
}

export interface BulkBranchComparisonResponse {
  groups: BranchComparisonGroupResponse[];
}

// Property-wide simulation batch

export type BatchLifecycleStatus =
  | 'SUBMITTED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'PARTIAL'
  | 'FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export interface PropertyBatchSubmissionResponse {
  batch_id: string;
  status: 'created' | 'deduplicated';
  /**
   * Underlying batch lifecycle status returned on dedup hits
   * When status='deduplicated' this tells the frontend
   * whether the reused batch is COMPLETED, PARTIAL, RUNNING, etc.  Absent on
   * freshly-created batches (status='created').
   */
  batch_status?: BatchLifecycleStatus | null;
  total_stand_count: number;
  /**
   * Number of stands that failed in the underlying batch (dedup hit only).
   * Non-zero when batch_status='PARTIAL' — the frontend should surface this
   * so the user knows which stands did not produce results.
   */
  failed_stand_count?: number;
  dispatched_count: number;
  deduplicated_run_count: number;
  failed_run_ids: string[];
  run_ids: string[];
}

export interface PropertyBatchRunSummary {
  run_id: string;
  stand_id: string;
  status: RunLifecycleStatus;
  decision_mode: string;
  branch_count: number;
  /** Operator/log text. Never shown to an owner — use `exclusion`. */
  error_message: string | null;
  /** Typed exclusion with registered copy for a non-completed run (2frvb). */
  exclusion?: PlanStandExclusionApi | null;
  created_at: string | null;
  completed_at: string | null;
}

/** forest_models.plan_stand_status.SimulationPlanUnavailableStand on the wire. */
export interface PlanStandExclusionApi {
  stand_id: string;
  reason: string;
  cause: string | null;
  detail: string;
  detail_fi: string;
}

export interface PropertyBatchDetailResponse {
  batch_id: string;
  property_id: string;
  decision_mode: string;
  status: BatchLifecycleStatus;
  total_stand_count: number;
  completed_stand_count: number;
  failed_stand_count: number;
  created_at: string | null;
  completed_at: string | null;
  triggered_by_user_id: number | null;
  runs: PropertyBatchRunSummary[];
  /**
   * Seconds to wait before re-reading the batch; null once it has stopped
   * progressing. Absent when the gateway could not derive it.
   */
  retry_after_seconds?: number | null;
}

export interface PropertyBatchStandPayload {
  stand_id: string;
  forest_stand: Record<string, unknown>;
  simulation_request: Record<string, unknown>;
}

export interface PropertyBatchSubmissionPayload {
  decision_mode: 'NO_MANAGEMENT' | 'AAF';
  shared_parameters?: Record<string, unknown>;
  triggered_by_user_id?: number | null;
  stands: PropertyBatchStandPayload[];
}

export class PropertyBatchAPI {
  /** Submit a property-wide simulation batch.
   *
   * Hits the gateway at /api/simulations/properties/{id}/batches which
   * proxies to backend_simulation.  Re-clicks within the dedup window
   * return the existing batch_id with status='deduplicated' (and
   * dispatched_count=0). */
  static async submitBatch(
    propertyId: string,
    payload: PropertyBatchSubmissionPayload,
  ): Promise<PropertyBatchSubmissionResponse> {
    const response = await simulationApiClient.post(
      `/api/simulations/properties/${propertyId}/batches`,
      payload,
    );
    return response.data;
  }

  /** Fetch the batch header + per-stand run summaries. */
  static async getBatch(propertyId: string, batchId: string): Promise<PropertyBatchDetailResponse> {
    const response = await apiClient.get(
      `/api/simulations/properties/${propertyId}/batches/${batchId}`,
    );
    return response.data;
  }

  /**
   * Delete one simulation batch and resolve only once it is gone.
   *
   * y496w batch-delete: the server purges a large batch in the background
   * and answers 202 `deletion_state: 'deleting'`; the state endpoint then
   * says `deleting` until the batch 404s (deleted) or reports `failed`. The
   * server derives every state; this only reads it.
   */
  static async deleteSimulationBatch(propertyId: string, batchId: string): Promise<void> {
    const path = `/api/simulations/properties/${propertyId}/batches/${batchId}`;
    const response = await apiClient.delete<PropertyBatchDeleteResponse>(path);
    if (response.data.deletion_state === 'deleted') return;
    if (response.data.deletion_state !== 'deleting') {
      throw new Error(
        `Batch ${batchId} delete answered deletion_state ` +
          `${JSON.stringify(response.data.deletion_state)}; expected 'deleted' or 'deleting'.`,
      );
    }
    const deadline = Date.now() + BATCH_DELETE_WAIT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELETE_POLL_MS));
      let state: PropertyBatchDeletionState;
      try {
        state = (
          await apiClient.get<PropertyBatchDeletionState>(`${path}/deletion`, {
            // 404 is the expected "deleted" answer here, not an error.
            skipErrorLogging: (error: unknown) => getResponseStatus(error) === 404,
          })
        ).data;
      } catch (error) {
        if (getResponseStatus(error) === 404) return;
        throw error;
      }
      if (state.deletion_state === 'failed') {
        throw new BatchDeletionFailedError(state.error_code, state.detail);
      }
      if (state.deletion_state !== 'deleting') {
        throw new Error(
          `Batch ${batchId} deletion state is ${JSON.stringify(state.deletion_state)} ` +
            'after an accepted delete.',
        );
      }
    }
    throw new Error(
      `Batch ${batchId} is still being deleted on the server after ` +
        `${BATCH_DELETE_WAIT_MS / 1000} s. Refresh later to see whether it finished.`,
    );
  }
}

const BATCH_DELETE_POLL_MS = 1500;
const BATCH_DELETE_WAIT_MS = 5 * 60 * 1000;

export interface PropertyBatchDeleteResponse {
  batch_id: string;
  deleted: boolean;
  deletion_state: 'deleted' | 'deleting';
}

export interface PropertyBatchDeletionState {
  batch_id: string;
  deletion_state: 'deleting' | 'failed' | 'not_requested';
  error_code: string | null;
  detail: string | null;
}

/** The server refused or failed an accepted batch delete; the batch is intact. */
export class BatchDeletionFailedError extends Error {
  readonly errorCode: string | null;

  constructor(errorCode: string | null, detail: string | null) {
    super(detail ?? `Batch deletion failed (${errorCode ?? 'no error code'}).`);
    this.name = 'BatchDeletionFailedError';
    this.errorCode = errorCode;
  }
}

export default EnhancedForestAPI;
